import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Card, CardContent, Button } from "@mui/material";
import OfficeImg from "../../Assets/office.jpg";
import TopNavBar from "../../Custom/TopNavBar";
import AvatarImg from "../../Assets/avatar-img.png"; // Default Avatar
import http from "../../../../../httpService"; // Assuming the API request is through this service
import { useParams } from "react-router-dom";
import "../../css/animation.css";
import CustomCursor from "../../Custom/CustomCursor";

const ProfileDetailsPage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const mainBoxRef = useRef(null);
  const [boxHeight, setBoxHeight] = useState(window.innerHeight);
  const [portfolioId, setPortfolioId] = useState(""); // To hold the portfolio ID
  const { uuid } = useParams();

  useEffect(() => {
    const portfolioId = sessionStorage.getItem("portolioId");
    if (portfolioId) {
      setPortfolioId(portfolioId); // Pass it to the top navigation or set it in state
    }
  }, []);

  // Fetching profile data from the API
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await http.get("/api/portfolio/employee/" + uuid, {
          headers: {
            Accept: "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        });
        if (response.data.success) {
          setProfile(response.data.data); // Set profile data
        } else {
          console.error("Failed to fetch profile data.");
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
      } finally {
        setLoading(false); // Set loading state to false after the request
      }
    };

    fetchProfile();
  }, []);

  // Update height when component mounts or updates
  useEffect(() => {
    const updateHeight = () => {
      if (mainBoxRef.current) {
        setBoxHeight(mainBoxRef.current.clientHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  if (loading) {
    return (
      <Typography sx={{ textAlign: "center", color: "white" }}>
        Loading...
      </Typography>
    );
  }

  if (!profile) {
    return (
      <Typography sx={{ textAlign: "center", color: "white" }}>
        No profile data available.
      </Typography>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: "relative",
          p: 2,
          minHeight: "100vh", // Minimum height to cover the entire viewport
          backgroundImage: `linear-gradient(to top, rgba(0, 0, 0, .97)12%, rgba(0, 0, 0, 0.25) 35%), 
                 linear-gradient(to bottom, rgba(0, 0, 0, 1) 5%, rgba(0, 0, 0, 0.25) 35%), 
                 url(${OfficeImg})`,
          backgroundSize: "150% 625px", // Fix the height of the background image and make it responsive
          backgroundPosition: "center",
          backgroundRepeat: "repeat-y", // Repeat background on the Y-axis if content exceeds height
          width: "100%", // Full width
          // overflow: "auto", // Allow scrolling if needed
          animation: "backgroundMove 10s infinite linear", // Apply background animation
        }}
      >
        <CustomCursor />

        <TopNavBar uuid={portfolioId} />

        {/* Content Card */}
        <Card
          ref={mainBoxRef}
          sx={{
            position: "relative",
            backgroundColor: "rgba(126, 126, 126, 0.2)",
            borderRadius: "24px",
            minHeight: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backdropFilter: "blur(3px)", // Increased blur
            border: "1px solid rgba(234, 234, 234, 0.2)",
            zIndex: 2,
          }}
        >
          <CardContent sx={{ padding: 2, width: "100%" }}>
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  width: "100%",
                  mt: " 13vh",
                }}
              >
                {/* Profile Details */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    // justifyContent: "space-between",
                    width: "70%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Card
                      sx={{
                        width: "66%",
                        minHeight: "30vh",
                        background: "rgba(103, 103, 103, .3)",
                        display: "flex",
                        flexDirection: "row",
                        backdropFilter: "blur(25px)", // Increased blur
                        borderRadius: "10px",
                        padding: 2,
                      }}
                    >
                      <Box
                        sx={{
                          backdropFilter: "blur(5px)", // Increased blur
                          display: "flex",
                          background: "rgba(230, 230, 230, 0.1)",
                          boxShadow: "0px 4px 10px rgba(2, 2, 2, 0.3)", // Added shadow
                          width: "48%",
                          height: "100%",
                          borderRadius: "10px",
                          padding: "0px 20px 0px 20px",
                        }}
                      >
                        <img
                          src={profile.image_url || AvatarImg}
                          alt="Profile"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>

                      <Box
                        sx={{
                          background: "transparent",
                          display: "flex",
                          width: "70%",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "start",
                          pl: 3,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "1rem",
                            color: "white",
                            mb: 1,
                          }}
                        >
                          {profile.role}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "2rem",
                            fontWeight: "500",
                            color: "white",
                            lineHeight: 1.1,
                            mb: 2,
                          }}
                        >
                          {profile.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "1rem",
                            color: "white",
                          }}
                        >
                          Candidate ID: {profile.candidate_id}
                        </Typography>
                        {/* Download CV Button */}
                        <Button
                          variant="contained"
                          sx={{
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            backdropFilter: "blur(15px)",
                            borderRadius: "10px",
                            mt: 1,
                            padding: "10px 12px",
                            color: "white",
                            textTransform: "capitalize",
                            fontFamily: "Poppins",
                            fontSize: ".8rem",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                            },
                          }}
                          onClick={() => {
                            const url = profile.cv_url; // Replace with your actual PDF URL
                            window.open(url, "_blank");
                          }}
                        >
                          Download CV
                        </Button>
                      </Box>
                    </Card>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "32%",
                        justifyContent: "space-between",
                      }}
                    >
                      <Card
                        sx={{
                          width: "100%",
                          // height: "21vh",
                          marginBottom: 1,
                          textAlign: "center",
                          background: "rgba(255, 255, 255, 0.1)",
                          backdropFilter: "blur(25px)", // Increased blur
                          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)", // Added shadow
                          borderRadius: "10px",
                          boxShadow: "1px 1px 10px 1px rgba(0, 0, 0, 0.2)",
                          padding: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "2.5rem",
                            color: "white",
                            textShadow: "1px 1px 5px rgba(0, 0, 0, 0.5)",
                            fontWeight: "600",
                          }}
                        >
                          {profile.year_of_experience}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "1rem",
                            color: "white",
                            fontWeight: "300",
                          }}
                        >
                          Years of Experience
                        </Typography>
                      </Card>
                      <Card
                        sx={{
                          // height: "21vh",
                          textAlign: "center",
                          background: "rgba(255, 255, 255, 0.1)",
                          backdropFilter: "blur(25px)", // Increased blur
                          borderRadius: "10px",
                          boxShadow: "1px 1px 10px 1px rgba(0, 0, 0, 0.2)",
                          padding: 4,
                          marginTop: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "1.2rem",
                            color: "white",
                            textShadow: "1px 1px 5px rgba(0, 0, 0, 0.5)",
                            fontWeight: "200",
                          }}
                        >
                          Location
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "1.1rem",
                            fontWeight: "500",
                            color: "white",
                          }}
                        >
                          {profile.location}
                        </Typography>
                      </Card>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: "100%",
                      textAlign: "start",
                      background: "rgba(0, 0, 0, 0.5)",
                      backdropFilter: "blur(25px)", // Increased blur
                      borderRadius: "10px",
                      boxShadow: "1px 1px 10px 1px rgba(0, 0, 0, 0.2)",
                      padding: 2,
                      flex: 1,
                      // minHeight: "29vh",
                      mt: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        fontFamily: "Poppins",
                        color: "#FFFFFF",
                      }}
                    >
                      Summary
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontFamily: "Poppins",
                        color: "#DEDEDE",
                        wordWrap: "break-word",
                      }}
                    >
                      {profile.summary}
                    </Typography>
                  </Box>
                </Box>

                {/* Skills and Certifications Section */}
                <Box
                  sx={{
                    width: { xs: "100%", md: "33%" }, // Full width on mobile, 33% on desktop
                    display: "flex",
                    flexDirection: "column",
                    ml: { xs: 0, md: 2 },
                    mt: { xs: 2, md: 0 },
                    height: "82vh", // Full viewport height
                  }}
                >
                  {/* Tech Expertise Section */}
                  <Box
                    sx={{
                      background: "rgba(206, 206, 206, 0.2)",
                      backdropFilter: "blur(15px)",
                      borderRadius: "10px",
                      padding: 2,
                      marginBottom: 2,
                      flex: "1 1 auto", // Allows flexible growth/shrink
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: "1.4rem",
                        fontWeight: "400",
                        color: "white",
                      }}
                    >
                      Tech Expertise
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                        overflowY: "auto",
                        maxHeight: "47vh",
                        pr: 1,
                        "&::-webkit-scrollbar": {
                          width: "6px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "rgba(255, 255, 255, 0.5)",
                          borderRadius: "10px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.7)",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "rgba(0, 0, 0, 0.2)",
                          borderRadius: "10px",
                        },
                      }}
                    >
                      {profile.tech_expertise.map((skill, skillIndex) => (
                        <Typography
                          key={skillIndex}
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "0.9rem",
                            color: "white",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            padding: "4px 8px",
                            borderRadius: "5px",
                          }}
                        >
                          {skill}
                        </Typography>
                      ))}
                    </Box>
                  </Box>

                  {/* Certifications Section */}
                  <Box
                    sx={{
                      background: "rgba(206, 206, 206, 0.2)",
                      backdropFilter: "blur(15px)",
                      borderRadius: "10px",
                      flex: "2 1 auto", // Takes up more space proportionally
                      padding: 2,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: "32vh", // Prevent overflow in flex container
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: "1.4rem",
                        fontWeight: "400",
                        color: "white",
                        marginBottom: 1,
                      }}
                    >
                      Certifications
                    </Typography>
                    <Box
                      sx={{
                        overflowY: "auto",
                        flex: 1, // Take remaining vertical space
                        pr: 1,
                        "&::-webkit-scrollbar": {
                          width: "6px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "rgba(255, 255, 255, 0.5)",
                          borderRadius: "10px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.7)",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "rgba(0, 0, 0, 0.2)",
                          borderRadius: "10px",
                        },
                      }}
                    >
                      {profile.certifications.map((cert, index) => (
                        <Typography
                          key={index}
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "0.9rem",
                            color: "white",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            padding: "4px 8px",
                            borderRadius: "5px",
                            marginBottom: "4px",
                            whiteSpace: "normal", // Allow line breaks
                            wordBreak: "break-word", // Break long words if needed
                            overflowWrap: "break-word", // Wrap overflow text properly
                          }}
                        >
                          {cert}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default ProfileDetailsPage;
